import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const NAV = [
  { label: "Features", to: "#product" },
  { label: "How it works", to: "#how-it-works" },
  { label: "Readiness", to: "#readiness" },
  { label: "Pricing", to: "#pricing" },
  { label: "FAQ", to: "#faq" },
];

export const MarketingHeader = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-zinc-950/60 backdrop-blur-xl border-b border-zinc-800/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <BrandLogo variant="lockup" iconSize={30} to="/" testid="mkt-brand" />
        <nav className="hidden md:flex items-center gap-7">
          {NAV.map((n) => (
            <a key={n.label} href={n.to} data-testid={`mkt-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
               className="text-[13px] text-zinc-400 hover:text-zinc-100 transition-colors">
              {n.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" data-testid="mkt-login-link" className="text-[13px] text-zinc-300 hover:text-zinc-50 px-3 py-2 transition-colors">
            Log in
          </Link>
          <Link to="/register" data-testid="mkt-header-cta"
                className="rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 text-[13px] font-semibold px-5 py-2.5 transition-colors">
            Start Preparing Free
          </Link>
        </div>
        <button type="button" data-testid="mkt-mobile-menu-btn" onClick={() => setOpen(!open)}
                className="md:hidden p-2 text-zinc-300" aria-label="Menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div data-testid="mkt-mobile-menu" className="md:hidden border-t border-zinc-800 bg-zinc-950/95 backdrop-blur-xl px-6 py-4 space-y-3">
          {NAV.map((n) => (
            <a key={n.label} href={n.to} onClick={() => setOpen(false)} className="block text-sm text-zinc-300 py-1">{n.label}</a>
          ))}
          <div className="pt-3 border-t border-zinc-800 flex items-center gap-3">
            <Link to="/login" className="text-sm text-zinc-300">Log in</Link>
            <Link to="/register" className="rounded-full bg-amber-500 text-zinc-950 text-sm font-semibold px-4 py-2">
              Start Preparing Free
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

const FOOTER_COLS = [
  {
    title: "Product",
    links: [
      { label: "Pricing", to: "/pricing" },
      { label: "Log in", to: "/login" },
      { label: "Create free account", to: "/register" },
    ],
  },
  {
    title: "Trust",
    links: [
      { label: "Trust Center", to: "/trust" },
      { label: "Security", to: "/trust/security" },
      { label: "AI Transparency", to: "/trust/ai-transparency" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/trust/privacy" },
      { label: "Terms of Service", to: "/trust/terms" },
      { label: "Cookie Policy", to: "/trust/cookies" },
    ],
  },
];

export const MarketingFooter = () => (
  <footer data-testid="mkt-footer" className="border-t border-zinc-800 bg-zinc-950">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-5 space-y-4">
        <BrandLogo variant="lockup" iconSize={28} to="/" testid="mkt-footer-brand" />
        <p className="text-sm text-zinc-500 leading-relaxed max-w-sm">
          Interview preparation built from your actual resume and the actual job
          description, with AI mock interviews, replay, and a measurable
          Interview Readiness Score.
        </p>
      </div>
      {FOOTER_COLS.map((col) => (
        <div key={col.title} className="md:col-span-2">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">{col.title}</div>
          <ul className="space-y-2.5">
            {col.links.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 text-xs text-zinc-600">
        © {new Date().getFullYear()} Intuiterview. All rights reserved.
      </div>
    </div>
  </footer>
);

export default function MarketingLayout({ children }) {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <MarketingHeader />
      <main className="pt-16">{children}</main>
      <MarketingFooter />
    </div>
  );
}
